import { motion } from "framer-motion";
import { fadeUp } from "../lib/motion";

const steps = [
  {
    n: "01",
    title: "Audit the business behind the audience",
    body: "We assess the current offer stack, monetization paths, conversion points, customer flow, and operational gaps.",
  },
  {
    n: "02",
    title: "Find where revenue is leaking",
    body: "We identify friction across the buyer journey, backend structure, sales flow, and delivery model.",
  },
  {
    n: "03",
    title: "Build the right systems",
    body: "We design and implement the pieces that create the biggest lift—offers, pathways, infrastructure, and monetization support.",
  },
  {
    n: "04",
    title: "Create a business that can carry growth",
    body: "The goal is not more activity. It's backend stability, stronger conversion, and a business that can scale without added chaos.",
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-section"
      className="relative py-24 md:py-32 lg:py-44 px-6 md:px-12 lg:px-20 max-w-[1500px] mx-auto"
    >
      <div className="max-w-3xl mb-16 lg:mb-24">
        <motion.div {...fadeUp} className="flex items-center gap-3 mb-6">
          <span className="eyebrow">07 — How It Works</span>
          <span className="block w-8 h-px bg-[#282930]" />
        </motion.div>
        <motion.h2
          {...fadeUp}
          className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight"
        >
          A focused engagement built around{" "}
          <span className="italic text-[#C9A26B]">the real bottlenecks.</span>
        </motion.h2>
      </div>

      <div className="space-y-0">
        {steps.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.05 }}
            className="relative grid grid-cols-12 gap-6 lg:gap-12 py-10 lg:py-16 border-t border-[#282930] last:border-b group"
          >
            <div className="col-span-12 md:col-span-3 lg:col-span-4 flex items-start">
              <span className="font-display text-[64px] sm:text-[80px] lg:text-[120px] leading-none text-[#F4EFE6]/[0.08] group-hover:text-[#C9A26B]/30 transition-colors duration-700">
                {s.n}
              </span>
            </div>
            <div className="col-span-12 md:col-span-9 lg:col-span-8 flex flex-col justify-center">
              <h3 className="font-display text-2xl md:text-3xl lg:text-4xl leading-tight tracking-tight text-[#F4EFE6]">
                {s.title}
              </h3>
              <p className="mt-4 text-[#A8A39D] text-base md:text-lg leading-relaxed max-w-[60ch]">
                {s.body}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.blockquote
        {...fadeUp}
        className="mt-20 lg:mt-28 max-w-3xl border-l-2 border-[#C9A26B] pl-6 text-[#A8A39D] italic font-display text-xl md:text-2xl leading-snug"
      >
        Every engagement is tailored. We do not force creators into a generic
        process or a one-size-fits-all growth system.
      </motion.blockquote>
    </section>
  );
};

export default HowItWorks;
